import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, AlertCircle, CheckCircle2, FileText } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { format, isToday, isThisWeek, isPast } from "date-fns";
import { TaskDetailSheet } from "@/components/task/TaskDetailSheet";
import { ListPageSkeleton } from "@/components/ui/skeleton-card";
import { EmptyState, ErrorState } from "@/components/ui/empty-state";
import { useToast } from "@/hooks/use-toast";

interface OutletContext {
  user: any;
  organizationId: string;
}

interface Task {
  id: string;
  title_snapshot: string;
  instruction_snapshot: string;
  status: string;
  due_at: string | null;
  is_critical_snapshot: boolean;
  required_proof_type_snapshot: string;
  sop_run_id: string;
  sop_run: {
    name: string;
    sop_template: {
      title: string;
    };
  };
}

const Tasks = () => {
  const { user, organizationId } = useOutletContext<OutletContext>();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("today");
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const { toast } = useToast();

  const fetchTasks = async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase
        .from("sop_run_steps")
        .select(`
          *,
          sop_run:sop_runs (
            name,
            sop_template:sop_templates (
              title
            )
          )
        `)
        .eq("assigned_to_user_id", user.id)
        .in("status", ["NOT_STARTED", "IN_PROGRESS"])
        .order("due_at", { ascending: true, nullsFirst: false });

      const { data, error: fetchError } = await query;

      if (fetchError) throw fetchError;

      let filteredTasks = data || [];

      if (activeTab === "today") {
        filteredTasks = filteredTasks.filter(
          (task) => task.due_at && isToday(new Date(task.due_at))
        );
      } else if (activeTab === "week") {
        filteredTasks = filteredTasks.filter(
          (task) => task.due_at && isThisWeek(new Date(task.due_at))
        );
      } else if (activeTab === "overdue") {
        filteredTasks = filteredTasks.filter(
          (task) => task.due_at && isPast(new Date(task.due_at))
        );
      }

      setTasks(filteredTasks as any);
    } catch (err: any) {
      console.error("Error fetching tasks:", err);
      setError(err.message || "Failed to load tasks");
      toast({
        variant: "destructive",
        title: "Error loading tasks",
        description: err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks();
  }, [organizationId, user, activeTab]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case "COMPLETED":
        return "bg-green-500/10 text-green-500";
      case "IN_PROGRESS":
        return "bg-blue-500/10 text-blue-500";
      case "OVERDUE":
        return "bg-destructive/10 text-destructive";
      default:
        return "bg-muted text-muted-foreground";
    }
  };

  const handleViewTask = (task: Task) => {
    setSelectedTask(task);
    setIsSheetOpen(true);
  };

  if (loading) {
    return <ListPageSkeleton count={4} />;
  }

  if (error) {
    return (
      <div className="container py-8 px-4">
        <ErrorState 
          title="Failed to load tasks" 
          description={error}
          onRetry={fetchTasks}
        />
      </div>
    );
  }

  return (
    <div className="container py-6 md:py-8 space-y-6 px-4 sm:px-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold mb-1 md:mb-2">My Tasks</h1>
        <p className="text-sm md:text-base text-muted-foreground">
          Tasks assigned to you across all active runs
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full sm:w-auto flex overflow-x-auto">
          <TabsTrigger value="today" className="flex-1 sm:flex-initial">Today</TabsTrigger>
          <TabsTrigger value="week" className="flex-1 sm:flex-initial">This Week</TabsTrigger>
          <TabsTrigger value="overdue" className="flex-1 sm:flex-initial">Overdue</TabsTrigger>
          <TabsTrigger value="all" className="flex-1 sm:flex-initial">All</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4 mt-6">
          {tasks.length === 0 ? (
            <EmptyState
              icon={CheckCircle2}
              title="All caught up!"
              description="No tasks in this category. Great work!"
            />
          ) : (
            tasks.map((task) => (
              <Card key={task.id} className="hover:shadow-md transition-shadow border-border">
                <CardHeader className="pb-3">
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <CardTitle className="text-base md:text-lg">{task.title_snapshot}</CardTitle>
                        {task.is_critical_snapshot && (
                          <Badge variant="destructive" className="text-xs">
                            Critical
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {task.sop_run?.sop_template?.title || "Unknown SOP"}
                        </span>
                        <span className="hidden sm:inline">•</span>
                        <span>{task.sop_run?.name || "Unknown Run"}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className={getStatusColor(task.status)}>
                        {task.status.replace("_", " ")}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {task.instruction_snapshot && (
                    <p className="text-sm text-muted-foreground">
                      {task.instruction_snapshot.substring(0, 200)}
                      {task.instruction_snapshot.length > 200 && "..."}
                    </p>
                  )}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                    <div className="flex flex-wrap items-center gap-2 md:gap-4 text-sm">
                      {task.due_at && (
                        <div
                          className={`flex items-center gap-1 ${
                            isPast(new Date(task.due_at))
                              ? "text-destructive"
                              : "text-muted-foreground"
                          }`}
                        >
                          {isPast(new Date(task.due_at)) ? (
                            <AlertCircle className="h-4 w-4" />
                          ) : (
                            <Clock className="h-4 w-4" />
                          )}
                          Due {format(new Date(task.due_at), "MMM d, h:mm a")}
                        </div>
                      )}
                      {task.required_proof_type_snapshot !== "NONE" && (
                        <Badge variant="outline" className="text-xs">
                          Proof: {task.required_proof_type_snapshot.replace("_", " ")}
                        </Badge>
                      )}
                    </div>
                    <Button size="sm" className="w-full sm:w-auto" onClick={() => handleViewTask(task)}>
                      View Details
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>

      <TaskDetailSheet
        open={isSheetOpen}
        onOpenChange={setIsSheetOpen}
        task={selectedTask}
        userId={user.id}
        onSuccess={fetchTasks}
      />
    </div>
  );
};

export default Tasks;
